import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchAmbulance } from '../Pages/AddCompanies/Action/action';
import { MapPoint, ClusterPoint, LocationType } from '../Interfaces/Map';
import { Company } from '../Interfaces/Company';
import { ShowAlert } from '../GlobalComponent/Alerts/Alert';
import { isValidCoordinate, fetchCoordinates } from "../GlobalComponent/MapView/mapUtils";

interface Ambulance {
  _id: string;
  ticket_id: string;
  serviceStatus: string;
  ambulanceStatus: string | null;
  ambulanceType: string;
  pickupLocation: {
    address: string;
    label: string;
    location: {
      type: string;
      coordinates: [number, number];
    };
  };
  dropLocation: {
    address: string;
    hospitalName?: string;
    label: string;
    location: {
      type: string;
      coordinates: [number, number];
    };
  }[];
  typeOfAssistanceRequired: {
    wheelChair: boolean;
    stretcher: boolean;
    paramedic: boolean;
    additionalHelp: boolean;
    numberofHelper: number;
    DoctorOnBoard: boolean;
  };
  amount: number | null;
  paymentStatus: string;
  pickupTime: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  companyId: string;
  companyName: string;
}

const useAmbulanceMap = ({ companies }: { companies: Company[] }) => {
  const [mapPoints, setMapPoints] = useState<MapPoint[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [clusters, setClusters] = useState<ClusterPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch ambulance data when component mounts or company changes
  useEffect(() => {
    const loadAmbulances = async () => {
      try {
        setLoading(true);
        const queryParams = selectedCompany ? { companyId: selectedCompany } : {};
        const response = await fetchAmbulance(queryParams);
        const points: MapPoint[] = generateMapPoints(response);

        if (points && Array.isArray(points)) {
          setMapPoints(points);
        } else {
          setMapPoints([]);
          ShowAlert('error', 'No valid ambulance data available');
        }
      } catch (error) {
        setMapPoints([]);
        ShowAlert('error', 'Error fetching ambulance data');
      } finally {
        setLoading(false);
      }
    };

    loadAmbulances();
    // eslint-disable-next-line
  }, [selectedCompany]);

  const generateMapPoints = (ambulances: Ambulance[]): MapPoint[] => {
    const points: MapPoint[] = [];
    const seenIds = new Set<string>();

    ambulances.forEach(ambulance => {
      // Skip if no pickup or drop locations
      if (!ambulance.pickupLocation || !ambulance.dropLocation?.length) {
        console.warn(`Skipping ambulance ${ambulance.ticket_id}: missing pickup or drop location`);
        return;
      }

      // Validate pickup coordinates
      const [pickupLat, pickupLng] = fetchCoordinates(ambulance.pickupLocation);

      if (!isValidCoordinate(pickupLat, pickupLng)) {
        console.warn(`Skipping ambulance ${ambulance.ticket_id}: invalid pickup coordinates`);
        return;
      }

      // Add pickup point
      const pickupId = `${ambulance.ticket_id}-pickup`;
      if (!seenIds.has(pickupId)) {
        seenIds.add(pickupId);
        points.push({
          id: pickupId,
          lat: pickupLat,
          lng: pickupLng,
          type: LocationType.PICKUP,
          ticketId: ambulance.ticket_id,
          companyName: ambulance.companyName,
          address: ambulance.pickupLocation.address
        });
      }

      // Add drop points
      ambulance.dropLocation.forEach((drop, index) => {
        const [dropLat, dropLng] = fetchCoordinates(drop);

        if (!isValidCoordinate(dropLat, dropLng)) {
          console.warn(`Skipping drop location ${index} for ambulance ${ambulance.ticket_id}: invalid coordinates`);
          return;
        }

        const dropId = `${ambulance.ticket_id}-drop-${index}`;
        if (!seenIds.has(dropId)) {
          seenIds.add(dropId);
          points.push({
            id: dropId,
            lat: dropLat,
            lng: dropLng,
            type: LocationType.DROP,
            ticketId: ambulance.ticket_id,
            companyName: ambulance.companyName,
            address: drop.address,
            hospitalName: drop.hospitalName
          });
        }
      });
    });
    return points;
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(company =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  const selectedCompanyName = companies.find(c => c._id === selectedCompany)?.companyName || "";

  // Clustering logic
  const createClusters = (points: MapPoint[], distance: number = 0.01): ClusterPoint[] => {
    const clusters: ClusterPoint[] = [];
    const processed = new Set<string>();

    points.forEach(point => {
      if (processed.has(point.id)) return;

      const nearbyPoints = points.filter(p => {
        if (processed.has(p.id) || p.id === point.id) return false;
        const dist = Math.sqrt(
          Math.pow(p.lat - point.lat, 2) + Math.pow(p.lng - point.lng, 2)
        );
        return dist <= distance;
      });

      if (nearbyPoints.length > 0) {
        const clusterPoints = [point, ...nearbyPoints];
        const pickupCount = clusterPoints.filter(p => p.type === LocationType.PICKUP).length;
        const dropCount = clusterPoints.filter(p => p.type === LocationType.DROP).length;

        let clusterType: LocationType;
        if (pickupCount > 0 && dropCount === 0) clusterType = LocationType.PICKUP;
        else if (dropCount > 0 && pickupCount === 0) clusterType = LocationType.DROP;
        else clusterType = LocationType.MIXED;

        const avgLat = clusterPoints.reduce((sum, p) => sum + p.lat, 0) / clusterPoints.length;
        const avgLng = clusterPoints.reduce((sum, p) => sum + p.lng, 0) / clusterPoints.length;

        clusters.push({
          id: `cluster-${clusters.length}`,
          lat: avgLat,
          lng: avgLng,
          count: clusterPoints.length,
          points: clusterPoints,
          type: clusterType
        });

        clusterPoints.forEach(p => processed.add(p.id));
      } else {
        clusters.push({
          id: `single-${point.id}`,
          lat: point.lat,
          lng: point.lng,
          count: 1,
          points: [point],
          type: point.type
        });
        processed.add(point.id);
      }
    });

    return clusters;
  };

  // Update clusters when mapPoints change
  useEffect(() => {
    const newClusters = createClusters(mapPoints);
    setClusters(newClusters);
  }, [mapPoints]);

  return {
    // State
    loading,
    companies,
    selectedCompany,
    setSelectedCompany,
    searchTerm,
    setSearchTerm,
    isOpen,
    setIsOpen,
    inputRef,
    mapPoints,
    clusters,

    // Computed values
    filteredCompanies,
    selectedCompanyName,
  };
};

export default useAmbulanceMap;